/**
 * Détail d'une campagne — portail admin. Même interface que le portail client
 * (`CampagneDetailView`, @repo/campagne), mais alimentée par les queries
 * `*SuperAdmin` (cross-org).
 *
 * CETTE PAGE est en consultation SAUF le référentiel, l'ajout de candidats et
 * l'envoi de leurs liens : `CAPABILITES_ADMIN_REFERENTIEL` ferme le lien
 * libre-service, mais ouvre l'onglet Référentiel en écriture — c'est la surface
 * de dépannage du support, sans laquelle une campagne au référentiel manquant ou
 * mal configuré reste bloquée côté client. Ce n'est pas une limite du portail
 * admin, qui écrit ailleurs — création de campagne (`CampagneCreerAdmin`,
 * /organisations/:orgId/campagnes/creer) et modification (`CampagneModifierAdmin`,
 * /campagnes/:id/modifier) comprises.
 */
import {
  CampagneDetailView,
  CAPABILITES_ADMIN_REFERENTIEL,
  useVueCampagne,
} from "@repo/campagne";
import { Breadcrumb, Button } from "@repo/ui";
import { useAuthToken } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { Pencil, Users } from "lucide-react";
import { Link, useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { arianeOrganisation, racineOrganisation } from "../../lib/ariane";

/** URL du déploiement, pour les routes `httpAction` (rapport PDF). */
const CONVEX_URL = process.env.BUN_PUBLIC_CONVEX_URL || "http://127.0.0.1:3210";

export function CampagneDetailAdmin() {
  const token = useAuthToken();
  const { id } = useParams<{ id: string }>();
  const campagneId = id as Id<"campagnes">;

  // Ce que la page affiche décide de ce qu'on charge : les passations d'un test
  // ne sont lues que par ses Résultats, son Matching et la Synthèse.
  const { chargerEvaluations } = useVueCampagne();

  const campagne = useQuery(api.campagnes.obtenirCampagneSuperAdmin, {
    campagneId,
  });
  const stats = useQuery(api.campagnes.obtenirStatsCampagneSuperAdmin, {
    campagneId,
  });

  const isEntStructCampagne =
    campagne?.evaluationsConfig?.some((e) => e.typeEval === "fairview") ??
    false;
  const isInbasketCampagne =
    campagne?.evaluationsConfig?.some((e) => e.typeEval === "inbasket") ?? false;
  const isPamiCampagne =
    campagne?.evaluationsConfig?.some(
      (e) => e.typeEval === "form" && e.template === "pami",
    ) ?? false;

  const evaluations = useQuery(
    api.evaluations.obtenirEvaluationsEntStructParCampagneSuperAdmin,
    isEntStructCampagne && chargerEvaluations("entstruct") ? { campagneId } : "skip",
  );
  const evaluationsInbasket = useQuery(
    api.evaluations.obtenirEvaluationsInbasketParCampagneSuperAdmin,
    isInbasketCampagne && chargerEvaluations("inbasket") ? { campagneId } : "skip",
  );
  const isFitCulturelCampagne =
    campagne?.evaluationsConfig?.some(
      (e) => e.typeEval === "form" && e.template === "pc-fit",
    ) ?? false;
  const evaluationsPami = useQuery(
    api.evaluations.obtenirEvaluationsPamiParCampagneSuperAdmin,
    isPamiCampagne && chargerEvaluations("pami") ? { campagneId } : "skip",
  );

  // Les queries `referentiel.*` portent déjà le bypass `isAdmin` (getCurrentUser
  // et non getOrgScopedUser) : pas de variante superAdmin à écrire. Les queries
  // `evaluations.*`, elles, sont org-scopées → variantes SuperAdmin.
  const referentiels = useQuery(
    api.referentiel.campagne.obtenirReferentielCampagne,
    { campagneId },
  );
  // Les passations fit-culturel se chargent dès que la campagne fait passer le
  // test — indépendamment du référentiel : la table de résultats les affiche
  // même sans référentiel configuré.
  const evaluationsFitCulturel = useQuery(
    api.evaluations.obtenirEvaluationsFitCulturelParCampagneSuperAdmin,
    isFitCulturelCampagne && chargerEvaluations("fitculturel") ? { campagneId } : "skip",
  );

  const isCopsoq3Campagne =
    campagne?.evaluationsConfig?.some(
      (e) => e.typeEval === "form" && e.template === "copsoq3",
    ) ?? false;
  const evaluationsCopsoq3 = useQuery(
    api.evaluations.obtenirEvaluationsCopsoq3ParCampagneSuperAdmin,
    isCopsoq3Campagne && chargerEvaluations("copsoq3") ? { campagneId } : "skip",
  );

  const campagnesHref = `${racineOrganisation(campagne?.organisationId)}/campagnes`;

  return (
    <CampagneDetailView
      campagneId={campagneId}
      campagne={campagne}
      stats={stats}
      evaluations={evaluations}
      evaluationsInbasket={evaluationsInbasket}
      evaluationsPami={evaluationsPami}
      evaluationsFitCulturel={evaluationsFitCulturel}
      evaluationsCopsoq3={evaluationsCopsoq3}
      referentiels={referentiels}
      token={token}
      convexUrl={CONVEX_URL}
      capabilities={CAPABILITES_ADMIN_REFERENTIEL}
      retourHref={campagnesHref}
      filAriane={
        <div className="mb-4 flex items-start justify-between gap-4">
          <Breadcrumb
            className="mb-0"
            items={[
              ...arianeOrganisation(campagne?.organisationId, campagne?.organisationNom, "campagnes"),
              { label: campagne?.nom ?? "Campagne" },
            ]}
          />
          {/* Seconde descente possible depuis une campagne : ses évaluateurs.
              Les candidats, eux, sont l'onglet Parcours de cette page. */}
          <div className="flex shrink-0 gap-2">
            <Link to={`/campagnes/${campagneId}/modifier`}>
              <Button variant="outline" size="sm">
                <Pencil className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            </Link>
            <Link to={`/campagnes/${campagneId}/evaluateurs`}>
              <Button variant="outline" size="sm">
                <Users className="h-4 w-4 mr-2" />
                Évaluateurs
              </Button>
            </Link>
          </div>
        </div>
      }
      getParcoursHref={(parcoursId) => `/parcours/${parcoursId}`}
      getEvaluationHref={(evaluation) => `/parcours/${evaluation.parcoursId}`}
      actionLabel="Voir le parcours"
    />
  );
}
